<template>
  <bean-source-page
    :type="type"
    :columns="columns"
    :form-columns="formColumns"
    :filter-columns="filterColumns"
    :actions="actions"
    :form="form"
    :title="$t('model.product')"
    resource="products"
    :onFormSubmit="handleSubmit"
  />
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import _ from 'lodash';
import { filterColumnsHash, columnsHash, formColumnsHash } from '../admin_configs/products/columns';

@Component
export default class Products extends Vue {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {}

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   */
  filterColumns() {
    return [
      { prop: 'title_cont' },
      {
        prop: 'category_id_eq',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            xRemotePreload: async () => {
              const { data } = await this.$request.get('/categories', { params: { parent_id_null: true, per_page: 100, expose_children: true } });
              return data.map(item => ({
                label: item.title,
                options: item.children.map(ele => ({ label: ele.title, value: ele.id }))
              }));
            },
            xRemoteSearch: async (keyword) => {
              const params = { parent_id_null: false, title_cont: keyword, expose_children: true };
              const { data } = await this.$request.get('/categories', { params });
              return data.map(item => ({
                label: item.title,
                options: item.children.map(ele => ({ label: ele.title, value: ele.id }))
              }));
            }
          }
        }
      },
      { prop: 'designer_id_eq' },
    ].filter(Boolean).map(hash => _.merge(filterColumnsHash[hash['prop']], hash))
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   */
  columns(h, { type }) {
    return [
      { prop: 'id' },
      { prop: 'title' },
      type === 'show' && {
        prop: 'desc',
        renderCell: (h, { props: { value } }) => {
          return <span style="white-space: pre-wrap;">{ value }</span>;
        }
      },
      { prop: 'cover' },
      { prop: 'category_id' },
      { prop: 'designer_id' },
      {
        prop: 'design_time',
        renderCell: (h, { props: { value } }) => {
          return <span>{ value }</span>;
        }
      },
      { prop: 'published_at' },
      type === 'show' && {
        prop: 'related_products',
        label: this.$t('product.relatedProducts'),
        renderCell: (h, { props: { value } }) => {
          return value.map(ele => {
            return <bean-link to={{ name: 'products.show', params: { id: ele.id } }}  style="margin-right: 10px">{ele.title}</bean-link>;
          });
        }
      },
      type === 'show' && { prop: 'images' },
      type === 'show' && { prop: 'share_image' },
      type === 'show' && { prop: 'mini_program_path', label: this.$t('product.miniProgramPath') },
      { prop: 'wxacode' },
      type === 'show' && { prop: 'created_at' },
      type === 'show' && { prop: 'updated_at' },
    ].filter(Boolean).map(hash => _.merge(columnsHash[hash['prop']], hash))
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   * @param {Object} params.data - 当前表单数据
   * @param {Function} params.change - 调用此方法同步表单数据 change({ field: 'new value' })
   */
  formColumns() {
    return [
      { prop: 'title' },
      {
        prop: 'desc',
        renderCell: {
          component: 'textarea'
        }
      },
      {
        prop: 'category_id',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            xRemotePreload: async () => {
              const { data } = await this.$request.get('/categories', { params: { parent_id_null: true, per_page: 100, expose_children: true } });
              return data.map(item => ({
                label: item.title,
                options: item.children.map(ele => ({ label: ele.title, value: ele.id }))
              }));
            },
            xRemoteSearch: async (keyword) => {
              const params = { parent_id_null: false, title_cont: keyword, expose_children: true };
              const { data } = await this.$request.get('/categories', { params });
              return data.map(item => ({
                label: item.title,
                options: item.children.map(ele => ({ label: ele.title, value: ele.id }))
              }));
            }
          }
        }
      },
      { prop: 'designer_id' },
      {
        prop: 'cover',
        renderCell: {
          hint: `${this.$t('suggestSize')} 163:88`,
        }
      },
      {
        prop: 'design_time',
        renderCell: {
          component: 'datePicker',
          props: {
            type: 'year'
          }
        }
      },
      {
        prop: 'images',
        renderCell: {
          hint: `${this.$t('suggestSize')} 375:288`,
          props: {
            limit: 9,
          }
        }
      },
      {
        prop: 'related_products',
        label: this.$t('product.relatedProducts'),
        renderCell: {
          component: 'listSelect',
          props: {
            trackBy: 'id',
            label: 'title',
            sort: false,
            defaultValue: [],
            filterColumns: [
              {
                prop: 'title',
                label: '',
                renderCell: {
                  component: 'input',
                  hint: this.$t('hints.searchByTitle')
                }
              }
            ],
            onFilter: async (params = {}) => {
              const { data } = await this.$request.get('/products', { title_cont: params['title'] });
              return data;
            },
          }
        }
      },
      {
        prop: 'share_image',
        renderCell: {
          hint: `${this.$t('suggestSize')} 5:4`,
        }
      },
    ].filter(Boolean).map(hash => _.merge(formColumnsHash[hash['prop']], hash))
  }

  /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  actions({ row }, { remove, reload }) {
    return [
      {
        component: 'routeButton',
        props: {
          label: this.$t('edit'),
          to: { name: 'products.edit', params: { id: row.id } }
        }
      },
      !row.published_at && {
        component: 'confirmButton',
        props: {
          label: this.$t('product.actions.publish'),
          type: 'primary',
          can: 'products.update'
        },
        on: {
          confirm: async () => {
            await this.$request.post(`/products/${row.id}/publish`);
            reload();
          }
        }
      },
      row.published_at && {
        component: 'confirmButton',
        props: {
          label: this.$t('product.actions.unpublish'),
          type: 'danger',
          can: 'products.update'
        },
        on: {
          confirm: async () => {
            await this.$request.post(`/products/${row.id}/unpublish`);
            reload();
          }
        }
      },
      {
        component: 'confirmButton',
        props: {
          label: this.$t('destroy'),
          type: 'danger',
          can: 'products.destroy'
        },
        on: {
          confirm: () => remove(row.id)
        }
      }
    ].filter(Boolean);
  }

  async handleSubmit({ data, type, resource }) {
    const relatedProductIds = _.get(data, 'related_products', []).map(item => item.id);
    if (type === 'new') {
      const response = await this.$request.post(`/${resource}`, {
        ...data,
        related_product_ids: relatedProductIds
      });
      this.$message.success(this.$t('bean.createSuccess'));
      this.$router.push({ name: `${resource}.show`, params: { id: response.id } });
    } else {
      await this.$request.put(`/${resource}/${data.id}`, {
        ...data,
        related_product_ids: relatedProductIds
      });
      this.$message.success(this.$t('bean.updateSuccess'));
      this.$router.push({ name: `${resource}.show`, params: { id: data.id } });
    }
  }
}

export const route = [
  {
    path: '/products',
    name: 'products.index',
    component: Products,
    props: {
      type: 'index',
    }
  },
  {
    path: '/products/new',
    name: 'products.new',
    component: Products,
    props: {
      type: 'new',
    }
  },
  {
    path: '/products/:id',
    name: 'products.show',
    component: Products,
    props: {
      type: 'show',
    }
  },
  {
    path: '/products/:id/edit',
    name: 'products.edit',
    component: Products,
    props: {
      type: 'edit'
    }
  }
]
</script>
